import React from 'react';
import type { TemplateModel } from '@troia-v3/types';
import { useModels } from '../hooks/useModels';
import { Icon } from '@/shared/icons'
interface ModelSelectorProps {
  onSelect: (model: TemplateModel) => void;
  selectedModelId?: string;
  /** When true, uses hardcoded light colors for modal context */
  isModal?: boolean;
}

/**
 * Grid of available website models/templates.
 * Displayed when creating a new website.
 */
export const ModelSelector: React.FC<ModelSelectorProps> = ({
  onSelect,
  selectedModelId,
  isModal = false,
}) => {
  const { data: models, isLoading } = useModels();

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-64 rounded-xl animate-pulse"
            style={{ backgroundColor: 'var(--theme-skeleton-background, #e5e7eb)' }}
          />
        ))}
      </div>
    );
  }

  if (!models || models.length === 0) {
    return (
      <div
        className="text-center py-12"
        style={{ color: isModal ? '#64748b' : 'var(--theme-text-secondary)' }}
      >
        <Icon name="globe" className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-lg font-medium">Nenhum modelo disponível</p>
        <p className="text-sm mt-1">Modelos de website serão adicionados em breve.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {models.map((model) => {
        const isSelected = selectedModelId === model.id;

        return (
          <button
            key={model.id}
            type="button"
            onClick={() => onSelect(model)}
            className={`relative text-left rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
              isSelected ? 'ring-2' : ''
            }`}
            style={{
              borderColor: isSelected
                ? '#8b5cf6'
                : isModal ? '#e5e7eb' : 'var(--theme-card-border)',
              backgroundColor: isModal ? '#ffffff' : 'var(--theme-card-background)',
            }}
          >
            {/* Preview image */}
            <div
              className="h-40 flex items-center justify-center"
              style={{ backgroundColor: isModal ? '#f1f5f9' : 'var(--theme-page-background)' }}
            >
              {model.previews?.thumbnail ? (
                <img
                  src={model.previews.thumbnail}
                  alt={model.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="globe" className="w-16 h-16 opacity-20"
                  style={{ color: isModal ? '#64748b' : 'var(--theme-text-secondary)' }} />
              )}
            </div>

            {/* Info */}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3
                  className="font-semibold text-sm"
                  style={{ color: isModal ? '#1e293b' : 'var(--theme-text-primary)' }}
                >
                  {model.name}
                </h3>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: isModal ? '#f1f5f9' : 'var(--theme-badge-background)',
                    color: isModal ? '#64748b' : 'var(--theme-badge-text)',
                  }}
                >
                  {model.segment}
                </span>
              </div>
              <p
                className="text-xs mt-1 line-clamp-2"
                style={{ color: isModal ? '#64748b' : 'var(--theme-text-secondary)' }}
              >
                {model.description}
              </p>
              <div className="flex items-center gap-3 mt-3">
                <span
                  className="text-xs"
                  style={{ color: isModal ? '#94a3b8' : 'var(--theme-text-tertiary)' }}
                >
                  {model.pages.length} páginas
                </span>
                <span
                  className="text-xs"
                  style={{ color: isModal ? '#94a3b8' : 'var(--theme-text-tertiary)' }}
                >
                  {model.blocks.length} componentes
                </span>
              </div>
            </div>

            {/* Selected indicator */}
            {isSelected && (
              <div
                className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                }}
              >
                <Icon name="check" className="w-4 h-4" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};
